/**
 * Native device apps (vfs-service stacks `swift-ios`, "iOS app (Swift)", SwiftUI + XcodeGen; `kotlin-android`,
 * "Android app (Kotlin)", Gradle + Jetpack Compose): they run on phones, never in the sandbox preview. As with a
 * command-line tool (utils/cliServices.js), Flow never starts one:
 *
 * - vfs-service names them in `GET /runnables` › `builders["services/<name>"]` (a builder `{ id: 'device', kind:
 *   'device', stack, platforms }`, `recommended: 'device'`, `device: { stack, label, platforms }`) and in `GET
 *   /device-builds` › `apps` (`framework: 'swift-ios' | 'kotlin-android'`); both answers are remembered per project
 *   (`rememberDeviceApps`), so code with only a project id can ask (`isDeviceItem`), as for the CLIs;
 * - the runner's entry of one says its stack (`language`), and a start asked anyway fails with "This is a native app:
 *   build it for devices…" (the gateway: 403 `native_app`): information, not a failure (`isNativeAppError`);
 * - Apps shows "iOS app" / "Android app" instead of a run state, no Open preview, no Start, and Build for devices;
 *   Start all skips them; How it runs is the one read-only "device" choice (`.flow/service.json` `{ "run": "device" }`).
 */
import { AndroidLogo, AppleLogo, DeviceMobile } from '@phosphor-icons/react';

/** The native stacks: their label (vfs-service's), the chip Apps shows, the platforms they build for, an icon. */
export const DEVICE_STACKS = {
  'swift-ios': { label: 'iOS app (Swift)', chip: 'iOS app', platforms: ['ios'], Icon: AppleLogo },
  'kotlin-android': { label: 'Android app (Kotlin)', chip: 'Android app', platforms: ['android'], Icon: AndroidLogo },
};

export const isDeviceStack = (id) => Object.prototype.hasOwnProperty.call(DEVICE_STACKS, String(id || ''));

/** The icon of a stack id: Apple / Android for the native ones, a phone for any other device app; null otherwise. */
export const deviceStackIcon = (id) => (isDeviceStack(id) ? DEVICE_STACKS[id].Icon : null);

/** The icon of a native app template (New project's gallery): by its id or one of its `stacks`; else null. */
export const templateDeviceIcon = (template) => deviceStackIcon(template?.id)
  || (Array.isArray(template?.stacks) ? template.stacks.map((stack) => deviceStackIcon(typeof stack === 'string' ? stack : stack?.id)).find(Boolean) : null)
  || null;

/** The device app of a stack id (`{ stack, label, chip, platforms }`), or the generic one ("Device app"). */
export function deviceAppOfStack(stack, label = null) {
  const known = isDeviceStack(stack) ? DEVICE_STACKS[stack] : null;
  return {
    stack: known ? stack : null,
    label: label || known?.label || 'Device app',
    chip: known?.chip || 'Device app',
    platforms: known?.platforms || [],
    Icon: known?.Icon || DeviceMobile,
  };
}

const NATIVE_TEXT = /This is a native app: build it for devices/;

/** Whether an error (a message, or an error with `code`) is the runner's / gateway's "This is a native app" refusal. */
export function isNativeAppError(error) {
  if (!error) { return false; }
  if (typeof error === 'string') { return NATIVE_TEXT.test(error); }
  return error.code === 'native_app' || NATIVE_TEXT.test(String(error.message || ''));
}

/** Whether vfs-service's builders of a service say it is a device app. */
export const isDeviceBuilders = (info) => Boolean(info) && (Boolean(info.device) || info.recommended === 'device'
  || (info.builders || []).some((builder) => builder.kind === 'device'));

/** The stack the builders name for a device app (`device.stack`, the builder's, the service's), or null. */
function buildersStack(info) {
  const builder = (info?.builders || []).find((item) => item.kind === 'device');
  return info?.device?.stack || builder?.stack || info?.stack || null;
}

/** Whether the runner's entry of a service is a device app: its stack, or a start it refused as a native app. */
export const isDeviceRunnerEntry = (service) => Boolean(service) && (isDeviceStack(service.language) || isDeviceStack(service.stack)
  || isNativeAppError(service.buildError) || isNativeAppError(service.processError));

/**
 * A service's device app (`{ stack, label, chip, platforms, Icon }`) from what is known of it: vfs-service's builders
 * (`builders`), its Build for devices entry (`app`, GET /device-builds › apps), the runner's entry (`service`); null
 * when it is not one.
 */
export function deviceAppOf({ builders = null, app = null, service = null } = {}) {
  if (isDeviceBuilders(builders)) {
    const builder = (builders.builders || []).find((item) => item.kind === 'device');
    return deviceAppOfStack(buildersStack(builders), builders.device?.label || builder?.label || null);
  }
  if (isDeviceStack(app?.framework)) { return deviceAppOfStack(app.framework, app.label || null); }
  if (isDeviceStack(service?.language)) { return deviceAppOfStack(service.language); }
  if (isDeviceStack(service?.stack)) { return deviceAppOfStack(service.stack); }
  if (isDeviceRunnerEntry(service)) { return deviceAppOfStack(null); }
  return null;
}

const known = new Map(); // projectId -> { runnables: { [service]: stack }, builds: { [service]: stack } }
const short = (name) => String(name || '').trim().replace(/^\.?\/+/, '').replace(/\/+$/, '');
const canonical = (name) => (short(name).includes('/') ? short(name) : `services/${short(name)}`);

function remember(projectId, source, map) {
  if (!projectId) { return; }
  const entry = known.get(projectId) || { runnables: {}, builds: {} };
  entry[source] = map;
  known.set(projectId, entry);
}

/** Remembers the device apps of a project's `GET /runnables` › `builders` (an answer without them clears them). */
export function rememberDeviceBuilders(projectId, builders) {
  const map = {};
  Object.entries(builders && typeof builders === 'object' ? builders : {}).forEach(([service, info]) => {
    if (isDeviceBuilders(info)) { map[canonical(service)] = buildersStack(info); }
  });
  remember(projectId, 'runnables', map);
}

/** Remembers the device apps of a project's `GET /device-builds` › `apps` (their framework a native stack). */
export function rememberDeviceBuildApps(projectId, apps) {
  const map = {};
  (Array.isArray(apps) ? apps : []).forEach((app) => {
    if (app?.service && isDeviceStack(app.framework)) { map[canonical(app.service)] = app.framework; }
  });
  remember(projectId, 'builds', map);
}

/** The device app `serviceName` of `projectId` is, from the answers remembered (`{ stack, label, chip, … }`), or null. */
export function deviceServiceOf(projectId, serviceName) {
  if (!projectId || !serviceName) { return null; }
  const entry = known.get(projectId);
  const name = canonical(serviceName);
  if (!entry || !(name in entry.runnables || name in entry.builds)) { return null; }
  return deviceAppOfStack(entry.runnables[name] || entry.builds[name] || null);
}

/**
 * The device app a Preview tab of `projectId` shows (`{ stack, label, chip, platforms, Icon }`), or null: the tab's own
 * (`device`), the runner's "This is a native app" refusal as its error, or a service an answer named as one, unless the
 * tab already shows a page (`url` with no error nor stopped state).
 */
export function deviceAppOfTab(projectId, tab) {
  if (!tab) { return null; }
  if (tab.device) { return tab.device; }
  const refused = isNativeAppError(tab.error) || isNativeAppError(tab.startError);
  if (!refused && tab.url && !tab.stopped) { return null; }
  return deviceServiceOf(projectId, tab.serviceName) || deviceAppOf({ service: tab.service }) || (refused ? deviceAppOfStack(null) : null);
}

/** Tests only: forget every project's device apps. */
export function resetDeviceApps() {
  known.clear();
}

/** Whether a run item (`{ serviceName, service }`) of `projectId` is a device app: its runner entry says so, or an answer did. */
export const isDeviceItem = (projectId, { serviceName, service } = {}) => isDeviceRunnerEntry(service) || Boolean(deviceServiceOf(projectId, serviceName));
